import { IconCalendarEvent, IconCheck, IconFilePlus, IconPhone } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { HeroError } from "./HeroCard";
import classes from "./home.module.scss";
import { faNumber } from "./format";
import { DayVisit, StylistDay, clockOf, minutesUntil, spanText } from "./stylistDay";

const invoicePath = (visit: DayVisit) => `/dashboard/payments/add/${visit.appointmentId}`;

type Props = {
  day: StylistDay | undefined;
  now: Date;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  // Shown once today's visits are over.
  earningsText?: string;
};

// The top of a stylist's home: the visit in progress, else the next one, else a calm summary of the day.
// One main action each; everything else lives in the list below or the bottom bar.
export default function NowCard({ day, now, isLoading, isError, onRetry, earningsText }: Props) {
  if (isLoading) return <NowSkeleton />;
  if (isError || !day) return <HeroError label="الان" onRetry={onRetry} />;

  if (day.live) return <LiveCard visit={day.live} now={now} />;
  if (day.next) return <NextCard visit={day.next} now={now} />;

  const finished = day.total > 0;
  return (
    <section className={`${classes.card} ${classes.summary}`} aria-label="جمع‌بندی امروز">
      <div className={classes.summaryHead}>
        <span className={classes.summaryIcon} data-tone={finished ? undefined : "calm"} aria-hidden>
          {finished ? <IconCheck size={22} stroke={2.2} /> : <IconCalendarEvent size={22} stroke={1.8} />}
        </span>
        <span className={classes.summaryText}>
          <strong>{finished ? "نوبت‌های امروز تمام شد" : "امروز نوبتی ندارید"}</strong>
          <span>
            {finished
              ? [`${faNumber(day.total)} نوبت`, earningsText && `کارکرد امروز ${earningsText}`].filter(Boolean).join(" · ")
              : "برای امروز نوبتی ثبت نشده است."}
          </span>
        </span>
      </div>
    </section>
  );
}

function LiveCard({ visit, now }: { visit: DayVisit; now: Date }) {
  return (
    <section className={classes.now} aria-label="نوبت در حال انجام">
      <div className={classes.nowTop}>
        <span className={classes.nowPill}>در حال انجام</span>
        <span>{spanText(minutesUntil(visit.end, now))} مانده</span>
      </div>
      <div className={classes.nowText}>
        <span className={classes.nowName}>{visit.customer}</span>
        <span className={classes.nowDetail}>
          {visit.service} · تا {clockOf(visit.end)}
        </span>
      </div>
      <div className={classes.nowActions}>
        {visit.invoiced ? (
          <span className={classes.nowPrimary} data-done>
            <IconCheck size={18} stroke={2} aria-hidden />
            فاکتور ثبت شد
          </span>
        ) : (
          <Link to={invoicePath(visit)} className={classes.nowPrimary}>
            <IconFilePlus size={18} stroke={1.8} aria-hidden />
            ثبت فاکتور
          </Link>
        )}
        {visit.phone && (
          <a href={`tel:${visit.phone}`} className={classes.nowIconButton} aria-label={`تماس با ${visit.customer}`}>
            <IconPhone size={19} stroke={1.8} aria-hidden />
          </a>
        )}
      </div>
    </section>
  );
}

function NextCard({ visit, now }: { visit: DayVisit; now: Date }) {
  return (
    <section className={classes.now} aria-label="نوبت بعدی">
      <div className={classes.nowTop}>
        <span>نوبت بعدی</span>
        <span className={classes.nowPill} data-tone="soon">
          {spanText(minutesUntil(visit.start, now))} دیگر
        </span>
      </div>
      <div className={classes.nowWho}>
        <span className={classes.nowClock}>{clockOf(visit.start)}</span>
        <span className={classes.nowText}>
          <span className={classes.nowName}>{visit.customer}</span>
          <span className={classes.nowDetail}>{visit.service}</span>
        </span>
      </div>
      {visit.phone && (
        <div className={classes.nowActions}>
          <a href={`tel:${visit.phone}`} className={classes.nowPrimary}>
            <IconPhone size={18} stroke={1.8} aria-hidden />
            تماس با مشتری
          </a>
        </div>
      )}
    </section>
  );
}

// One line for finished visits still waiting for an invoice; the latest one is a tap away.
export function InvoiceReminder({ visits }: { visits: DayVisit[] }) {
  const latest = visits[0];
  if (!latest) return null;
  return (
    <Link to={invoicePath(latest)} className={classes.reminder}>
      <IconFilePlus size={20} stroke={1.8} aria-hidden />
      <span className={classes.reminderText}>
        {visits.length === 1 ? `فاکتور نوبت ${latest.customer} ثبت نشده` : `${faNumber(visits.length)} نوبت امروز فاکتور ندارند`}
      </span>
      <span className={classes.reminderAction}>ثبت فاکتور</span>
    </Link>
  );
}

function NowSkeleton() {
  return (
    <section className={classes.now} aria-label="الان" aria-busy="true">
      <span className={classes.shimmer} style={{ width: 90, height: 20 }} />
      <span className={classes.shimmer} style={{ width: 170, height: 24 }} />
      <span className={classes.shimmer} style={{ height: 46, borderRadius: 13 }} />
    </section>
  );
}
